import * as THREE from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { solidworksDefaultPreset } from '../render/solidworks-default-preset'
import { asRecord } from './utils'

/**
 * GLTFLoader parser 的最小访问结构。
 *
 * 设计说明：
 * 1. Three.js 没有把 parser 的内部 JSON 与 associations 暴露为稳定类型；
 * 2. 这里只读取材质归一化所需的最小字段，避免导入链路依赖 GLTFParser 具体实现。
 */
interface ImportedGltfParserAccess {
  /** 原始 glTF JSON，用于判断材质是否显式声明 PBR 金属度/粗糙度。 */
  json?: unknown
  /** Three.js runtime 对象到 glTF JSON 下标的关联表。 */
  associations?: unknown
}

/**
 * glTF 原始材质记录。
 *
 * 设计说明：
 * 1. 只描述 normalizer 关心的字段；
 * 2. 未列出的字段仍保留在索引签名里，便于通过 `asRecord` 做安全读取。
 */
interface GltfMaterialRecord extends Record<string, unknown> {
  /** glTF 标准 PBR 金属度/粗糙度定义。 */
  pbrMetallicRoughness?: unknown
  /** 导出器附加的业务元数据。 */
  extras?: unknown
  /** glTF 材质扩展定义。 */
  extensions?: unknown
}

/**
 * 运行时材质归一化上下文。
 */
interface ImportedMaterialNormalizationContext {
  /** 原始 glTF JSON 记录。 */
  parserRecord: Record<string, unknown> | null
  /** runtime 对象与 glTF JSON 的关联表。 */
  associations: Map<unknown, unknown> | null
  /** 当前 GLB 是否带有 CAD/STEP 语义。 */
  hasCadModelMetadata: boolean
}

/**
 * 材质归一化判定输入。
 */
interface ImportedMaterialNormalizationInput {
  /** 需要检查的 runtime 材质。 */
  material: THREE.MeshStandardMaterial
  /** 该材质对应的 glTF 原始材质记录。 */
  materialRecord: GltfMaterialRecord | null
  /** 当前 GLB 是否带有 CAD/STEP 语义。 */
  hasCadModelMetadata: boolean
}

/**
 * 默认 glTF 金属度/粗糙度的误差阈值。
 */
const GLTF_DEFAULT_PBR_EPSILON = 1e-6

/**
 * 导入模型原始表面的 polygon offset factor。
 *
 * 说明：
 * 原始表面向深度缓冲区后方偏移，Selected 覆盖模型保持正常深度，
 * 从而避免两套共享 geometry 的 Mesh 发生深度竞争。
 */
const IMPORTED_MODEL_SURFACE_POLYGON_OFFSET_FACTOR = 1

/**
 * 导入模型原始表面的 polygon offset units。
 */
const IMPORTED_MODEL_SURFACE_POLYGON_OFFSET_UNITS = 1

/**
 * 判断材质是否为 MeshStandardMaterial 族。
 *
 * @param material runtime 材质。
 * @returns 是标准 PBR 材质时返回 `true`。
 */
function isMeshStandardMaterial(material: THREE.Material): material is THREE.MeshStandardMaterial {
  return material instanceof THREE.MeshStandardMaterial
    || (material as THREE.Material & { isMeshStandardMaterial?: boolean }).isMeshStandardMaterial === true
}

/**
 * 判断 runtime 材质是否仍处于 GLTFLoader 的默认金属度/粗糙度。
 *
 * 设计说明：
 * 1. glTF 规范默认 `metallicFactor=1`、`roughnessFactor=1`；
 * 2. CAD 颜色材质没有真实 PBR 语义时，如果保留该默认值，会让环境光漫反射贡献被金属度吃掉；
 * 3. 这里只修正仍停留在默认值的材质，避免覆盖导入后已经被业务显式调整过的材质。
 *
 * @param material runtime 材质。
 * @returns 材质仍为 glTF 默认 PBR 参数时返回 `true`。
 */
function isGltfDefaultMetalRoughness(material: THREE.MeshStandardMaterial): boolean {
  return Math.abs(material.metalness - 1) <= GLTF_DEFAULT_PBR_EPSILON
    && Math.abs(material.roughness - 1) <= GLTF_DEFAULT_PBR_EPSILON
}

/**
 * 判断材质是否只携带纯色信息。
 *
 * @param material runtime 材质。
 * @returns 没有贴图/环境贴图驱动 PBR 时返回 `true`。
 */
function isPlainColorMaterial(material: THREE.MeshStandardMaterial): boolean {
  return !material.map
    && !material.metalnessMap
    && !material.roughnessMap
    && !material.envMap
}

/**
 * 判断 STEP 元数据是否表示纯颜色实体。
 *
 * @param value 原始 STEP 元数据。
 * @returns 是 STEP 颜色实体时返回 `true`。
 */
function isStepColorRecord(value: unknown): boolean {
  const record = asRecord(value)
  if (!record) {
    return false
  }

  const entityType = typeof record.entityType === 'string'
    ? record.entityType.trim().toUpperCase()
    : ''
  const type = typeof record.type === 'string'
    ? record.type.trim().toUpperCase()
    : ''

  return entityType === 'COLOUR_RGB'
    || entityType === 'COLOUR_RGBA'
    || type === 'COLOUR_RGB'
    || type === 'COLOUR_RGBA'
}

/**
 * 从单个候选对象中读取 STEP 颜色元数据。
 *
 * @param value 原始候选对象。
 * @returns 存在 STEP 颜色实体时返回 `true`。
 */
function hasStepColorMetadataInSource(value: unknown): boolean {
  const record = asRecord(value)
  if (!record) {
    return false
  }

  const extrasRecord = asRecord(record.extras)
  return isStepColorRecord(record)
    || isStepColorRecord(record.step)
    || isStepColorRecord(extrasRecord?.step)
}

/**
 * 判断多个候选对象中是否存在 STEP 颜色元数据。
 *
 * @param sources 原始候选对象列表。
 * @returns 任一候选对象带 STEP 颜色实体时返回 `true`。
 */
function hasStepColorMetadata(...sources: unknown[]): boolean {
  return sources.some((source) => hasStepColorMetadataInSource(source))
}

/**
 * 判断 glTF 材质是否显式声明了金属度/粗糙度语义。
 *
 * @param materialRecord 原始材质记录。
 * @returns 材质显式声明 PBR 金属度/粗糙度时返回 `true`。
 */
function hasExplicitMetalRoughness(materialRecord: GltfMaterialRecord | null): boolean {
  const pbrRecord = asRecord(materialRecord?.pbrMetallicRoughness)
  if (!pbrRecord) {
    return false
  }

  return Object.prototype.hasOwnProperty.call(pbrRecord, 'metallicFactor')
    || Object.prototype.hasOwnProperty.call(pbrRecord, 'roughnessFactor')
    || asRecord(pbrRecord.metallicRoughnessTexture) !== null
}

/**
 * 判断 glTF 材质是否通过扩展声明了材质模型。
 *
 * @param materialRecord 原始材质记录。
 * @returns 存在材质模型扩展时返回 `true`。
 */
function hasMaterialModelExtension(materialRecord: GltfMaterialRecord | null): boolean {
  const extensionsRecord = asRecord(materialRecord?.extensions)
  if (!extensionsRecord) {
    return false
  }

  return Object.keys(extensionsRecord).some((extensionName) =>
    extensionName.startsWith('KHR_materials_') || extensionName.startsWith('EXT_materials_'),
  )
}

/**
 * 判断 glTF JSON 是否带有 CAD/STEP 语义。
 *
 * 设计说明：
 * 1. 该判断只用于限定材质修正范围，不参与拓扑/装配语义；
 * 2. 没有 CAD 语义的普通 GLB 保留 glTF 标准默认材质行为，避免误改外部资产。
 *
 * @param parserRecord glTF 原始 JSON 记录。
 * @returns 当前 GLB 带 CAD/STEP 语义时返回 `true`。
 */
function hasCadParserMetadata(parserRecord: Record<string, unknown> | null): boolean {
  if (!parserRecord) {
    return false
  }

  const assetRecord = asRecord(parserRecord.asset)
  const assetExtrasRecord = asRecord(assetRecord?.extras)
  const rootExtrasRecord = asRecord(parserRecord.extras)
  if (
    asRecord(assetExtrasRecord?.step)
    || asRecord(assetExtrasRecord?.stepMetadata)
    || asRecord(rootExtrasRecord?.stepMetadata)
    || asRecord(parserRecord.stepMetadata)
  ) {
    return true
  }

  const meshValues = Array.isArray(parserRecord.meshes) ? parserRecord.meshes : []
  return meshValues.some((meshValue) => {
    const meshRecord = asRecord(meshValue)
    const meshExtrasRecord = asRecord(meshRecord?.extras)
    return Boolean(
      asRecord(meshExtrasRecord?.step)
      || asRecord(meshExtrasRecord?.brep)
      || asRecord(meshExtrasRecord?.topologyRef)
      || asRecord(meshRecord?.step)
      || asRecord(meshRecord?.brep),
    )
  })
}

/**
 * 读取 GLTFLoader parser 的最小访问结构。
 *
 * @param gltf GLTFLoader 解析结果。
 * @returns parser 访问结构。
 */
function resolveParserAccess(gltf: GLTF): ImportedGltfParserAccess {
  return gltf.parser as unknown as ImportedGltfParserAccess
}

/**
 * 读取 glTF 原始 JSON 记录。
 *
 * @param gltf GLTFLoader 解析结果。
 * @returns glTF 原始 JSON；不存在或非法时返回 `null`。
 */
function resolveParserRecord(gltf: GLTF): Record<string, unknown> | null {
  return asRecord(resolveParserAccess(gltf).json)
}

/**
 * 读取 GLTFLoader runtime 关联表。
 *
 * @param gltf GLTFLoader 解析结果。
 * @returns runtime 关联表；不存在时返回 `null`。
 */
function resolveAssociations(gltf: GLTF): Map<unknown, unknown> | null {
  const associations = resolveParserAccess(gltf).associations
  return associations instanceof Map ? associations : null
}

/**
 * 读取 runtime 材质对应的 glTF 材质下标。
 *
 * @param material runtime 材质。
 * @param associations runtime 关联表。
 * @returns glTF 材质下标；不存在时返回 `undefined`。
 */
function resolveRuntimeMaterialIndex(
  material: THREE.Material,
  associations: Map<unknown, unknown> | null,
): number | undefined {
  const associationRecord = asRecord(associations?.get(material))
  const materialIndex = typeof associationRecord?.materials === 'number'
    ? associationRecord.materials
    : NaN
  return Number.isInteger(materialIndex) && materialIndex >= 0 ? materialIndex : undefined
}

/**
 * 读取 glTF 原始材质记录。
 *
 * @param parserRecord glTF 原始 JSON 记录。
 * @param materialIndex glTF 材质下标。
 * @returns 原始材质记录；不存在时返回 `null`。
 */
function resolveMaterialRecord(
  parserRecord: Record<string, unknown> | null,
  materialIndex: number | undefined,
): GltfMaterialRecord | null {
  if (!parserRecord || materialIndex === undefined) {
    return null
  }

  const materialValues = Array.isArray(parserRecord.materials) ? parserRecord.materials : []
  return asRecord(materialValues[materialIndex]) as GltfMaterialRecord | null
}

/**
 * 判断导入材质是否应该按 CAD 纯色材质归一化。
 *
 * @param input 归一化判定输入。
 * @returns 需要归一化时返回 `true`。
 */
function shouldNormalizeImportedColorMaterial(input: ImportedMaterialNormalizationInput): boolean {
  if (!isGltfDefaultMetalRoughness(input.material) || !isPlainColorMaterial(input.material)) {
    return false
  }

  if (
    hasExplicitMetalRoughness(input.materialRecord)
    || hasMaterialModelExtension(input.materialRecord)
  ) {
    return false
  }

  if (hasStepColorMetadata(input.material.userData, input.materialRecord)) {
    return true
  }

  return input.hasCadModelMetadata
}

/**
 * 归一化单个 runtime 材质。
 *
 * @param material runtime 材质。
 * @param context 材质归一化上下文。
 * @returns 发生归一化时返回 `true`。
 */
function normalizeRuntimeMaterial(
  material: THREE.Material,
  context: ImportedMaterialNormalizationContext,
): boolean {
  if (!isMeshStandardMaterial(material)) {
    return false
  }

  const materialIndex = resolveRuntimeMaterialIndex(material, context.associations)
  const materialRecord = resolveMaterialRecord(context.parserRecord, materialIndex)
  if (!shouldNormalizeImportedColorMaterial({
    material,
    materialRecord,
    hasCadModelMetadata: context.hasCadModelMetadata,
  })) {
    return false
  }

  material.metalness = solidworksDefaultPreset.material.baseMetalness
  material.roughness = solidworksDefaultPreset.material.baseRoughness
  material.needsUpdate = true
  return true
}

/**
 * 为单个导入模型表面材质设置稳定的正向深度偏移。
 *
 * @param material GLTFLoader 创建的 Mesh 材质。
 * @returns 材质参数发生变化时返回 true。
 */
function normalizeRuntimeSurfaceDepthOffset(material: THREE.Material): boolean {
  if (
    material.polygonOffset
    && material.polygonOffsetFactor === IMPORTED_MODEL_SURFACE_POLYGON_OFFSET_FACTOR
    && material.polygonOffsetUnits === IMPORTED_MODEL_SURFACE_POLYGON_OFFSET_UNITS
  ) {
    return false
  }

  material.polygonOffset = true
  material.polygonOffsetFactor = IMPORTED_MODEL_SURFACE_POLYGON_OFFSET_FACTOR
  material.polygonOffsetUnits = IMPORTED_MODEL_SURFACE_POLYGON_OFFSET_UNITS
  material.needsUpdate = true
  return true
}

/**
 * 归一化导入模型全部原始表面材质的深度偏移。
 *
 * 设计说明：
 * 1. 只遍历 Mesh 材质，不修改拓扑线、辅助线、精灵或后处理材质；
 * 2. 单材质和材质数组走同一入口，共享材质只处理一次；
 * 3. 该偏移在 GLB 加载完成后立即建立，后续整模型 Selected 只需创建正常深度的覆盖 Mesh；
 * 4. 函数只修改已有材质参数，不创建新资源，因此不增加释放职责。
 *
 * @param gltf GLTFLoader 解析结果。
 * @returns 本次发生参数变化的材质数量。
 */
export function normalizeImportedModelSurfaceDepthOffsets(gltf: GLTF): number {
  const normalizedMaterials = new Set<THREE.Material>()
  let normalizedCount = 0

  gltf.scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) {
      return
    }

    const materials = Array.isArray(object.material) ? object.material : [object.material]
    materials.forEach((material) => {
      if (normalizedMaterials.has(material)) {
        return
      }

      normalizedMaterials.add(material)
      if (normalizeRuntimeSurfaceDepthOffset(material)) {
        normalizedCount += 1
      }
    })
  })

  return normalizedCount
}

/**
 * 归一化导入模型中的 CAD 纯色材质。
 *
 * 设计说明：
 * 1. SOLIDWORKS 的默认灯光包含环境光，纯颜色几何体不应因为 glTF 默认 `metalness=1` 导致暗面接近黑色；
 * 2. 只处理 CAD/STEP 语义下无显式 PBR 参数的纯色 MeshStandardMaterial，避免覆盖真正金属材质；
 * 3. 该函数只修改已有材质参数，不创建新的 three.js 资源，因此不引入额外释放路径。
 *
 * @param gltf GLTFLoader 解析结果。
 * @returns 被归一化的材质数量。
 */
export function normalizeImportedModelColorOnlyMaterials(gltf: GLTF): number {
  const parserRecord = resolveParserRecord(gltf)
  const context: ImportedMaterialNormalizationContext = {
    parserRecord,
    associations: resolveAssociations(gltf),
    hasCadModelMetadata: hasCadParserMetadata(parserRecord),
  }
  const normalizedMaterials = new Set<THREE.Material>()
  let normalizedCount = 0

  gltf.scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) {
      return
    }

    const materials = Array.isArray(object.material) ? object.material : [object.material]
    materials.forEach((material) => {
      if (normalizedMaterials.has(material)) {
        return
      }

      normalizedMaterials.add(material)
      if (normalizeRuntimeMaterial(material, context)) {
        normalizedCount += 1
      }
    })
  })

  return normalizedCount
}
